import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Curated brand directory — always shown regardless of stock
const CURATED_BRANDS = [
  'Acne Studios', 'Alexander McQueen', 'Amiri', 'A-Cold-Wall*',
  'Balenciaga', 'Bottega Veneta', 'Brunello Cucinelli', 'Burberry',
  'Balmain', 'Casablanca', 'Chanel', 'Christian Louboutin', 'Chrome Hearts',
  'Dior', 'Dolce & Gabbana', 'Fear Of God Essentials', 'Fendi', 'Gallery Dept.',
  'Givenchy', 'Gucci', 'Hermès', 'Human Made',
  'Jacquemus', 'Kenzo', 'Lanvin', 'Loewe', 'Loro Piana',
  'Louis Vuitton', 'Maison Margiela', 'Marine Serre', 'Moncler', 'New Balance',
  'Off-White', 'Palm Angels', 'Prada', 'Represent',
  'Rhude', 'Rick Owens', 'Saint Laurent', 'Stone Island',
  'Stüssy', 'The Row', 'Tory Burch', 'Valentino', 'Versace', 'Zegna',
];

export default function Designers() {
  const [activeDesigners, setActiveDesigners] = useState(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/products/designers`)
      .then(r => { setActiveDesigners(new Set(r.data)); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Merge curated list with any active designers not in curated list
  const allBrands = [...new Set([...CURATED_BRANDS, ...activeDesigners])].sort();
  const filtered = allBrands.filter(d => d.toLowerCase().includes(search.toLowerCase()));

  const grouped = filtered.reduce((acc, d) => {
    const letter = d[0].toUpperCase();
    if (!acc[letter]) acc[letter] = [];
    acc[letter].push(d);
    return acc;
  }, {});

  const alphabet = Object.keys(grouped).sort();

  return (
    <div className="bg-white min-h-screen pt-20" data-testid="designers-page">
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-12">
        <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3 font-work">Directory</p>
        <div className="flex items-end justify-between mb-12">
          <h1 className="font-playfair text-5xl text-[#0A0A0A] tracking-tight">Our Designers</h1>
          <p className="text-sm text-[#999999] font-work hidden sm:block">{allBrands.length} houses</p>
        </div>

        {/* Search */}
        <div className="relative max-w-sm mb-16">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#CCCCCC]" />
          <input
            type="text"
            placeholder="Search designers..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full border border-[#E5E5E5] focus:border-[#D4AF37] outline-none pl-10 pr-4 py-3 text-sm font-work text-[#0A0A0A]"
            data-testid="designer-search"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="flex flex-col gap-12">
            {alphabet.map(letter => (
              <div key={letter}>
                <p className="font-playfair text-5xl text-[#E5E5E5] font-bold mb-6">{letter}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {grouped[letter].map(d => {
                    const hasProducts = activeDesigners.has(d);
                    return hasProducts ? (
                      <Link
                        key={d}
                        to={`/shop?designer=${encodeURIComponent(d)}`}
                        className="group py-3 border-b border-[#E5E5E5] hover:border-[#D4AF37] transition-colors"
                        data-testid={`designer-${d.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        <span className="text-sm font-work text-[#0A0A0A] group-hover:text-[#D4AF37] transition-colors">{d}</span>
                      </Link>
                    ) : (
                      <div
                        key={d}
                        className="py-3 border-b border-[#F0F0F0]"
                        data-testid={`designer-coming-soon-${d.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        <span className="text-sm font-work text-[#BBBBBB]">{d}</span>
                        <p className="text-[10px] uppercase tracking-[0.15em] text-[#D4AF37] font-work mt-0.5">New drops coming soon</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
